import { capitalCase } from "change-case";

import { loadLocale } from "@smart/itops-locale-dom";
import { Avatar, Button, IconName } from "@smart/itops-sb-design-system-dom";

import { useIntakeSettingsNavigation } from "./navigate";
import { SettingsTab, SettingsTabKey, useSettingsTabs } from "./tabs";

type SettingsLauncherDetails = { icon: IconName; description: string };

type SettingsLauncherItem = SettingsTab & SettingsLauncherDetails;

const buildSettingsLauncherDetails = (
  terms: ReturnType<typeof loadLocale>["terms"],
): Record<SettingsTabKey, SettingsLauncherDetails> => ({
  "web-enquiries": {
    icon: "regularGlobe",
    description: "See your live forms and who's assigned to them.",
  },
  "client-messages": {
    icon: "regularMessages",
    description: "Manage the messages sent to clients.",
  },
  general: {
    icon: "regularGear",
    description: "Choose your practice areas and default forms.",
  },
  branding: {
    icon: "regularWindow",
    description: `${capitalCase(terms.customise.future)} how forms look and feel.`,
  },
  "voice-guide": {
    icon: "regularWaveformLines",
    description: "Turn on voice-guided answers for clients.",
  },
});

const SettingsLauncherButton = ({
  item,
  disabled,
  onClick,
}: {
  item: SettingsLauncherItem;
  disabled: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    className="flex gap-medium items-center hover:bg-neutral-20 rounded py-[0.4rem] px-[1.2rem] disabled:opacity-50"
    disabled={disabled}
    onClick={onClick}
  >
    <Avatar icon={item.icon} size={32} tabIndex={-1} />
    <div className="flex flex-col items-start">
      <div className="text-paragraph font-semibold">{item.label}</div>
      <div className="text-ui text-neutral-500">{item.description}</div>
    </div>
  </button>
);

export const SettingsLauncher = () => {
  const { terms } = loadLocale();
  const details = buildSettingsLauncherDetails(terms);
  const items: SettingsLauncherItem[] = useSettingsTabs().map((tab) => ({
    ...tab,
    ...details[tab.key],
  }));

  const { canOpen, openWindow } = useIntakeSettingsNavigation();

  return (
    <div className="flex flex-col gap-large h-full">
      <div className="flex justify-between items-center">
        <span className="text-h3 font-poppins">Intake forms</span>
        <Button
          text="Manage settings"
          size="small"
          variant="secondaryDefault"
          disabled={!canOpen}
          rightIcon="regularArrowUpRightAndArrowDownLeftFromCenter"
          onClick={() => openWindow()}
        />
      </div>
      <div className="flex flex-col gap-small">
        {items.map((item) => (
          <SettingsLauncherButton
            key={item.key}
            item={item}
            disabled={!canOpen}
            onClick={() => openWindow(item.key)}
          />
        ))}
      </div>
    </div>
  );
};
